import { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, useMediaQuery, Typography } from '@material-ui/core';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { Spinner } from 'components/UI';
import MembershipsIsEmpty from './List/MembershipsIsEmpty';
import DefaultHeader from 'components/UI/V2/DefaultHeader/DefaultHeader';
import { SelectionBar } from 'components/UI/SelectionBar/SelectionBar';
import SearchBar from 'components/UI/V3/SearchBar/SearchBar';
import { Table } from './List/Table/Table';
import { AddMembership } from 'components/Memberships/Add/AddMembership';
import MembershipDetails from './Detail/MembershipDetails';
import { Membership } from '@spike/membership-model';
import { faPlus, faSearch } from '@fortawesome/pro-solid-svg-icons';
import { fetchMembershipsThunk, MembershipsStatus, changeMembershipStatusThunk } from '@spike/memberships-action';
import { useApiClientWrapper } from 'hooks';
import { RootState } from 'store';
import useNonInitialEffect from '@versiondos/hooks';
import { useHasPremiumPlan } from 'hooks/premiumPlan';
import PremiumFeatureAlert from 'components/UI/PremiumFeatureAlert';
import { PaymentsPath, ServicesPath } from 'routes/Paths';
import { useHistory } from 'react-router-dom';
import { PaymentsStatus } from '@spike/payments-action';

interface Props {
    create?: string;
    serviceId?: number;
    onBack?: () => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            width: '100%',
            minHeight: '100%',
            display: 'flex',
            [theme.breakpoints.down('sm')]: {
                display: 'block'
            }
        },
        headWrapper: {
            backgroundColor: '#FAFAFA',
            paddingLeft: 16,
            paddingRight: 16,
            paddingBottom: 10,
            paddingTop: 20,
            [theme.breakpoints.up('md')]: {
                paddingLeft: '25px',
                paddingRight: '25px',
                paddingTop: 28
            }
        },
        wrapperContainer: {
            padding: '0',
            width: '100%',
            height: '100%'
        },
        wrapperContainerEmpty: {
            paddingTop: 30,
            paddingBottom: 30,
            paddingLeft: 24,
            paddingRight: 24
        },
        loadingContainer: {
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
        },
        headerContainer: {
            paddingLeft: 0,
            paddingRight: 0,
            marginBottom: 25
        },
        addMembershipContainer: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            zIndex: 11000,
            backgroundColor: '#fff',
            width: '100%',
            height: '100%',
            padding: '20px'
        },
        title: {
            marginBottom: theme.spacing(2)
        },
        SelectionBar: {
            [theme.breakpoints.up('md')]: {
                backgroundColor: '#FFF',
                marginBottom: 10
            }
        },
        FullWidthContainer: {
            width: '100%'
        },
        tableContainer: {
            paddingBottom: '30px',
            [theme.breakpoints.down('sm')]: {
                marginTop: '10px'
            }
        },
        containerEmpty: {
            padding: '40px 20px 20px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center'
        },
        imageEmpty: {
            width: '100%',
            maxWidth: '375px',
            display: 'inline-block'
        },
        titleEmpty: {
            marginTop: '20px',
            fontSize: '22px',
            fontWeight: 600,
            color: '#000'
        },
        descriptionEmpty: {
            marginTop: '10px',
            fontSize: '16px',
            color: '#000',
            maxWidth: '300px',
            marginLeft: 'auto',
            marginRight: 'auto'
        },
        titleNoMemberships: {
            maxWidth: '300px',
            marginLeft: 'auto',
            marginRight: 'auto'
        },
        searchBarContainer: {
            paddingTop: 18,
            paddingBottom: 18
        }
    })
);

export const MembershipsComponent: FunctionComponent<Props> = props => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const [loading, setLoading] = useState(true);
    const memberships = useSelector<RootState, Membership[]>(state => state.memberships.list);
    const membershipsStatus = useSelector<RootState, MembershipsStatus>(state => state.memberships.status);
    const [showAddMembership, setShowAddMembership] = useState(false);
    const [selectedMembership, setSelectedMembership] = useState<Membership | null>(null);
    const [selectedOption, setSelectedOption] = useState('active');
    const [filteredMemberships, setFilteredMemberships] = useState<Membership[]>([]);
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const [searchText, setSearchText] = useState('');
    const hasPremiumPlan = useHasPremiumPlan();
    const history = useHistory();
    const paymentsEnabled = useSelector<RootState, boolean | undefined>(
        state => state.payments.enrollmentStatus?.enabled
    );
    const paymentStatus = useSelector<RootState, PaymentsStatus>(state => state.payments.status);
    const [isPaymentsLoading, setIsPaymentsLoading] = useState(true);
    const [showWarning, setShowWarning] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);

    const fetchMemberships = () => {
        dispatch(fetchMembershipsThunk(apiClientWrapper));
    };

    useEffect(() => {
        fetchMemberships();
    }, []);

    useNonInitialEffect(() => {
        switch (membershipsStatus) {
            case MembershipsStatus.GetSuccess:
                setLoading(false);
                if (selectedMembership && memberships.length > 0) {
                    const membership = memberships.find(membership => membership.id === selectedMembership.id);
                    if (membership) {
                        setSelectedMembership(membership);
                    }
                }
                break;
            case MembershipsStatus.Error:
                setLoading(false);
                break;
            case MembershipsStatus.ChangingStatus:
                setLoading(true);
                break;
            case MembershipsStatus.ChangeStatusSuccess:
                fetchMemberships();
                break;
            case MembershipsStatus.ChangeStatusError:
                setLoading(false);
                break;
        }
    }, [membershipsStatus]);

    useNonInitialEffect(() => {
        switch (paymentStatus) {
            case PaymentsStatus.GetEnrollmentStatusSuccess:
                setIsPaymentsLoading(false);
                break;
            case PaymentsStatus.GettingEnrollmentStatus:
                setIsPaymentsLoading(true);
                break;
            case PaymentsStatus.Error:
                setIsPaymentsLoading(false);
                break;
        }
    }, [paymentStatus]);

    useEffect(() => {
        const filterMemberships = () => {
            let filtered = memberships;

            // Filter by status
            switch (selectedOption) {
                case 'active':
                    filtered = filtered.filter(membership => membership.active);
                    break;
                case 'inactive':
                    filtered = filtered.filter(membership => !membership.active);
                    break;
                case 'all':
                default:
                    break;
            }

            // Filter by search text
            if (searchText.length >= 3) {
                filtered = filtered.filter(membership =>
                    membership.name.toLowerCase().includes(searchText.toLowerCase())
                );
            }

            return filtered;
        };

        setFilteredMemberships(filterMemberships());
    }, [selectedOption, memberships, searchText]);

    const handleToggleStatus = (membershipId: number, isActive: boolean) => {
        dispatch(changeMembershipStatusThunk(apiClientWrapper, membershipId, isActive));
    };

    useEffect(() => {
        if (!loading && !isPaymentsLoading) {
            if (!hasPremiumPlan || !paymentsEnabled) {
                setShowWarning(true);
            } else {
                setShowWarning(false);
            }
            setInitialLoading(false);
        }
    }, [loading, isPaymentsLoading, hasPremiumPlan, paymentsEnabled]);

    const handleCloseWarning = () => {
        setShowWarning(false);
        history.push(ServicesPath);
    };

    if (showAddMembership) {
        return <AddMembership onClose={() => setShowAddMembership(false)} />;
    }

    if (selectedMembership) {
        return (
            <MembershipDetails
                membership={selectedMembership}
                onClose={() => setSelectedMembership(null)}
                refreshMemberships={fetchMemberships}
            />
        );
    }

    const filterOptions = [
        { id: 'active', name: 'Active' },
        { id: 'inactive', name: 'Inactive' },
        { id: 'all', name: 'All' }
    ];

    const handleSelect = (id: string) => {
        setSelectedOption(id);
    };

    const renderEmptyState = () => (
        <Box className={classes.containerEmpty}>
            <img
                className={classes.imageEmpty}
                src="/images/memberships/clients-notfound.svg"
            />
            <Typography className={classes.titleEmpty}>No memberships found</Typography>
            <Typography className={classes.descriptionEmpty}>
                You currently don&apos;t have any memberships to display.
            </Typography>
        </Box>
    );

    // TODO: Remove this after testing
    //memberships.length = 0;

    const renderContent = () => {
        if (loading || isPaymentsLoading) {
            return (
                <Box className={classes.loadingContainer}>
                    <Spinner />
                </Box>
            );
        }

        if (memberships.length === 0) {
            return (
                <Box className={`${classes.wrapperContainer} ${classes.wrapperContainerEmpty}`}>
                    <MembershipsIsEmpty
                        titleClassName={classes.titleNoMemberships}
                        onClick={() => setShowAddMembership(true)}
                    />
                </Box>
            );
        }

        return (
            <Box className={classes.wrapperContainer}>
                {filteredMemberships && (
                    <>
                        <Box className={classes.headWrapper}>
                            <DefaultHeader
                                title={'Memberships'}
                                buttonLabel={isMobile ? 'Add' : 'Add Membership'}
                                displayButton={true}
                                onClick={() => setShowAddMembership(true)}
                                id="memberships_add_button"
                                className={classes.headerContainer}
                                buttonIcon={faPlus}
                                textBelowTitle={
                                    'This is a long title to test how it looks and how it works on seveal resolutions'
                                }
                            />

                            <SelectionBar
                                options={filterOptions}
                                selectedId={selectedOption}
                                onSelect={handleSelect}
                                className={classes.SelectionBar}
                            />

                            <SearchBar
                                searchText={searchText}
                                onChangeSearchText={setSearchText}
                                placeholder="Search by membership name"
                                className={classes.searchBarContainer}
                            />
                        </Box>
                        <Box className={classes.FullWidthContainer}>
                            <Table
                                page={filteredMemberships}
                                totalRecords={filteredMemberships.length}
                                pageNumber={1}
                                pageSize={10}
                                loading={false}
                                onChangePage={() => {}}
                                onChangePageSize={() => {}}
                                onEditMembership={() => {}}
                                onSelectMembership={setSelectedMembership}
                                hasNextPage={false}
                                className={classes.tableContainer}
                                onChangeStatus={handleToggleStatus}
                            />
                            {!loading && filteredMemberships.length === 0 && renderEmptyState()}
                        </Box>
                    </>
                )}
            </Box>
        );
    };

    return (
        <Box className={classes.root}>
            {initialLoading ? (
                <Box className={classes.loadingContainer}>
                    <Spinner />
                </Box>
            ) : showWarning ? (
                <PremiumFeatureAlert
                    isOpen={showWarning}
                    onCancel={handleCloseWarning}
                    text={
                        !hasPremiumPlan
                            ? 'Unlock memberships with a premium plan'
                            : 'Memberships require an active CuddlesPay account'
                    }
                />
            ) : (
                renderContent()
            )}
        </Box>
    );
};

export default MembershipsComponent;
